import { useCallback, useLayoutEffect } from 'react'
import { createGlobalStyle } from '@nfinite/react-hubstairs/styled'
import { useId } from '@react-aria/utils'

//warning: In some instances, using blur on #root can cause a layout shift
export const FreezeBodyStyle = createGlobalStyle`
  body {
    #root {
      transition: filter 150ms;
    }
  }

  body[data-freeze="true"] {
    overflow: hidden;
    &[data-blur="true"] {
      #root {
        filter: blur(2px);
      }
    }
  }
`

function useGetAttributes() {
  const bodyNode = document.body

  return useCallback(() => {
    const current = bodyNode.dataset.freezeIds || '{}'
    const currentConfig = JSON.parse(current)
    const currentIds = Object.keys(currentConfig)
    return { currentConfig, currentIds }
  }, [bodyNode])
}

function useSetAttributes() {
  const bodyNode = document.body

  return useCallback(
    ({ currentConfig }) => {
      bodyNode.dataset.freezeIds = JSON.stringify(currentConfig)
      bodyNode.dataset.freeze = Object.keys(currentConfig).length > 0
      bodyNode.dataset.blur = Object.values(currentConfig).some(({ blur }) => blur)
    },
    [bodyNode],
  )
}

export default function useFreezeBody(isOpen, blur = true) {
  const id = useId()

  const getAttributes = useGetAttributes()
  const setAttributes = useSetAttributes()

  const open = useCallback(
    ({ id, ...config }) => {
      const { currentConfig, currentIds } = getAttributes()

      if (!currentIds.includes(id)) {
        currentConfig[id] = config
      }

      // Issue that this solves:
      // In draft catalog page the Bulk actions bar is always visible
      // when we open the drawer, freezebody sets overflow: hidden
      // and the scrollbar disappears making the floating bar shift right
      if (Object.keys(currentConfig).length === 1) {
        // Measure the scrollbar width before overflow: hidden hides it.
        // save it as css var --scrollbar-width so it can be used in Bulk actions bar css
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`)
      }

      setAttributes({ currentConfig })
    },
    [getAttributes, setAttributes],
  )

  const close = useCallback(
    ({ id }) => {
      const { currentConfig, currentIds } = getAttributes()

      if (currentIds.includes(id)) {
        delete currentConfig[id]
      }

      if (Object.keys(currentConfig).length === 0) {
        document.documentElement.style.setProperty('--scrollbar-width', '0px')
      }

      setAttributes({ currentConfig })
    },
    [getAttributes, setAttributes],
  )

  useLayoutEffect(() => {
    if (isOpen) {
      open({ id, blur })
    } else {
      close({ id })
    }

    return () => {
      close({ id })
    }
  }, [open, close, id, isOpen, blur])
}
